import memoize from "micro-memoize";
import PropTypes from "prop-types";
import { useState } from "react";
import { getExistingRows } from "../../Table/utils";
export const ITEM_SELECTION = "item";
export const PAGE_SELECTION = "page";
export const ALL_PAGES_SELECTION = "all";
/**
 * It gets only the items that exist in the data set.
 */

const getExistingItems = memoize((data, getItemKey, items) => getExistingRows(data, getItemKey, items));
/**
 * `List` is a render prop component that allows you to manage a list (add, remove, disable)
 * @type {Component}
 */

function List(props) {
  const {
    defaultItems,
    disabledItems,
    children,
    onChange,
    selectionType,
    items,
    data,
    getItemKey,
    remotePagination,
    currentPageData,
    allExpanded,
    totalItems
  } = props;
  const [listItems, setListItems] = useState(defaultItems);

  function areItemsControlled() {
    return items !== null;
  }

  function getSelectedItems() {
    const currentItems = areItemsControlled() ? items : listItems;
    /*
     * When remote pagination is enabled, we can't tell if the
     * items exist in the data set or not because we don't have the whole data set.
     */

    return remotePagination ? currentItems : getExistingItems(data, getItemKey, currentItems);
  }

  function getTotalItems() {
    return totalItems ? totalItems : data.length;
  }
  /**
   * Return the total count of items that are not disabled.
   */


  function getEnabledTotalItems() {
    return getTotalItems() - disabledItems.length;
  }
  /**
   * Return the total count of selected items that are not disabled.
   */


  function getEnabledSelectedItems() {
    const enabledSelectedItemsCount = getSelectedItems().reduce((count, item) => {
      const isDisabled = disabledItems.indexOf(item) !== -1;

      if (!isDisabled) {
        count += 1;
      }

      return count;
    }, 0);
    return enabledSelectedItemsCount;
  }

  const getSelectedItemsOnPage = () => {
    const totalSelectedRowsOnPage = [];

    if (currentPageData) {
      currentPageData.forEach(item => {
        const itemKey = getItemKey(item); // If rowKey exists is found in selectedRows (disabled or enabled).

        if (getSelectedItems().indexOf(itemKey) > -1) {
          totalSelectedRowsOnPage.push(itemKey);
        }
      });
    }

    return totalSelectedRowsOnPage;
  };
  /**
   * Used to determine if the column header Checkbox is indeterminate.
   */


  const isSelectAllIndeterminate = () => {
    const existingItemsCount = getSelectedItems().length;
    const totalItems = getTotalItems();
    return existingItemsCount > 0 && existingItemsCount < totalItems;
  };
  /**
   * Reconciles selected items with the selectAll action.
   * @param currentPageSelection `true` if it's a selection/unselection on the current page.
   * @returns Object Returns an object of arrays: totalSelectedItems and disabledAndSelected.
   */


  const reconcileSelected = currentPageSelection => {
    const totalSelectedItems = [];
    const disabledAndSelected = [];
    const items = getSelectedItems();
    (currentPageSelection ? currentPageData : data).forEach(n => {
      const itemKey = getItemKey(n);

      if ( // If rowKey exists in disabledItems and is also in items.
      disabledItems.indexOf(itemKey) > -1 && items.indexOf(itemKey) > -1) {
        disabledAndSelected.push(itemKey);
        return false;
      } else if ( // If rowKey exists in disabledItems and not in items.
      disabledItems.indexOf(itemKey) > -1 && items.indexOf(itemKey) < 0) {
        return false;
      }

      totalSelectedItems.push(itemKey);
    });
    return {
      totalSelectedItems,
      disabledAndSelected
    };
  };
  /**
   * Selects or unselects multiple items.
   * @param select `true` for selection, `false` for unselection.
   * @param pageSelection `true` for page, `false` for all pages.
   */


  const handleMultipleSelection = (select, pageSelection) => {
    const {
      totalSelectedItems,
      disabledAndSelected
    } = reconcileSelected(pageSelection);
    const type = pageSelection ? PAGE_SELECTION : ALL_PAGES_SELECTION;

    if (areItemsControlled()) {
      onChange({
        items: totalSelectedItems,
        select,
        type
      });
    } else {
      let newItems = [];

      if (select) {
        // Return an array without duplicate rows.
        newItems = [...new Set([...getSelectedItems(), ...totalSelectedItems, ...disabledAndSelected])];
      } else {
        newItems = getSelectedItems().filter(id => totalSelectedItems.indexOf(id) === -1);
      }

      setListItems(newItems);
      onChange && onChange({
        items: totalSelectedItems,
        select,
        type
      });
    }
  };
  /**
   * Selects or unselects all visible items (current page).
   * @param select `true` for selection, `false` for unselection.
   */


  const handleSelectionAllVisible = select => {
    handleMultipleSelection(select, true);
  };
  /**
   * Selects or unselects all items from all pages
   * @param select `true` for selection, `false` for unselection.
   */


  const handleSelectionAll = select => {
    handleMultipleSelection(select, false);
  };
  /**
   * Selects or unselects single items.
   * @param {*} id The rowKey id of the item (i.e. items[rowkey value])
   * @param {boolean} isRowSelected
   */


  const handleSingleSelection = (id, isRowSelected) => {
    const singleSelection = selectionType === "single";
    const type = ITEM_SELECTION;

    if (areItemsControlled()) {
      onChange({
        items: [id],
        select: isRowSelected,
        type
      });
    } else {
      let totalItemselections = [];
      const items = getSelectedItems();
      const currentselectedItems = [...items];

      if (isRowSelected) {
        // Select row.
        totalItemselections = singleSelection ? [id] : currentselectedItems.concat(id);
      } else {
        // Unselect row.
        currentselectedItems.splice(currentselectedItems.indexOf(id), 1);
        totalItemselections = currentselectedItems;
      }

      setListItems(totalItemselections);
      onChange({
        items: [id],
        select: isRowSelected,
        type
      });
    }
  };

  function getInternalState() {
    const totalSelectedItemsOnPage = getSelectedItemsOnPage().length;
    const totalAvailableRows = currentPageData ? currentPageData.length : 0;
    const totalSelectedItems = getSelectedItems().length;
    return {
      items: getSelectedItems(),
      allEnabledSelected: totalSelectedItems > 0 && getEnabledSelectedItems() === getEnabledTotalItems(),
      allSelected: allExpanded ? allExpanded : totalSelectedItems === getTotalItems(),
      allSelectedOnPage: totalSelectedItemsOnPage !== 0 && totalSelectedItemsOnPage === totalAvailableRows,
      pageIndeterminate: totalSelectedItemsOnPage > 0 && totalSelectedItemsOnPage !== totalAvailableRows,
      allIndeterminate: isSelectAllIndeterminate(),
      disabledItems,
      onSelectAllVisible: handleSelectionAllVisible,
      onSelectAll: handleSelectionAll,
      onSelect: handleSingleSelection,
      selectionType
    };
  }

  return children(getInternalState());
}

List.propTypes = {
  /** Item key. */
  getItemKey: PropTypes.func.isRequired,

  /**
   * Boolean prop determining if all rows should be expanded.
   */
  allExpanded: PropTypes.bool,

  /**
   * The Table component to be extended.
   */
  children: PropTypes.func,

  /** Current page data set */
  currentPageData: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** All data set. */
  data: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** An array of `id` values that indicate the corresponding items set is selected. */
  defaultItems: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** Array of ids to disable. */
  disabledItems: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /** An array of `id` values that indicate the corresponding items set is selected. */
  items: PropTypes.array,
  // eslint-disable-line react/forbid-prop-types

  /**
   * A callback function that fires whenever there's a current page selection (toggling the row checkbox,
   * header checkbox or clicking the "Select/Unselect all on this page" menu items). It receives an object
   * containing an array of selected ids.
   */
  onChange: PropTypes.func,

  /**
   * Indicates if the pagination is remote. (e.g. server side)
   */
  remotePagination: PropTypes.bool,

  /**
   * Indicates how the row item can be selected.  `multi` adds checkboxes,
   * `single` adds radiobuttons, and `row` add the selectable row feature.
   */
  selectionType: PropTypes.oneOf(["multi", "single"]),

  /**
   * Provides the total rows available across the items set.
   */
  totalItems: PropTypes.number
};
List.defaultProps = {
  allExpanded: false,
  children: null,
  currentPageData: null,
  data: [],
  defaultItems: [],
  disabledItems: [],
  items: null,
  onChange: () => {},
  remotePagination: false,
  selectionType: "multi",
  totalItems: null
};
export default List;